import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, Platform, ScrollView, Text, View } from 'react-native';
import ErrorState from '../ErrorState';
import SpotLegend from '../SpotLegend';
import RoomLayoutGrid from './RoomLayoutGrid';
import SpotGridZoomViewportLazy from './SpotGridZoomViewportLazy';
import { getZoomViewportHeight } from './spotGridZoomBounds';
import { getPinchZoomMaxSlotHeight } from './spotGridLegendReserve';
import type { RoomLayoutMapProps } from './RoomLayoutMap.types';
import type { RoomLayoutApiResponse, RoomLayoutError, RoomLayoutSpot } from './RoomLayoutMap.types';
import { RoomLayoutErrorCode, SpotStatus } from './RoomLayoutMap.types';
import { RoomLayoutMapStrings } from './RoomLayoutMap.strings';
import { styles } from './RoomLayoutMap.styles';
import { enrichSpotsWithLayoutSpotTypePrefix } from './spotTypeEnrichment';
import {
  deriveSpotTypeLegendItemsFromSpots,
  spotTypeLegendItemsFromSpotTypesForBookableSpots
} from './spotTypeLegend';
import { applySpotsOverlayForMap } from './applySpotsOverlayForMap';
import { countBookableSpots } from './computeBookableSpotsForMap';
import { resolveSpotKeyInLayout, spotMatchesKey } from './spotKey';
import { useRoomLayoutMap } from './useRoomLayoutMap';
import { useScrollToCenter } from './useScrollToCenter';

function errorToTitleMessage(error: RoomLayoutError): { title: string; message: string } {
  const {
    ERROR_FALLBACK_MESSAGE,
    ERROR_TITLE_LAYOUT_UNAVAILABLE,
    ERROR_MESSAGE_NOT_FOUND,
    ERROR_MESSAGE_LAYOUT_INACTIVE,
    ERROR_TITLE_NETWORK,
    ERROR_MESSAGE_CHECK_CONNECTION,
    ERROR_TITLE_SOMETHING_WRONG
  } = RoomLayoutMapStrings;
  switch (error.code) {
    case RoomLayoutErrorCode.NOT_FOUND:
      return { title: ERROR_TITLE_LAYOUT_UNAVAILABLE, message: ERROR_MESSAGE_NOT_FOUND };
    case RoomLayoutErrorCode.LAYOUT_INACTIVE:
      return {
        title: ERROR_TITLE_LAYOUT_UNAVAILABLE,
        message: ERROR_MESSAGE_LAYOUT_INACTIVE
      };
    // Timeouts, no internet, and fetch failures: always use connection message, never 404 or error.message.
    case RoomLayoutErrorCode.CONNECTION_ERROR:
      return {
        title: ERROR_TITLE_NETWORK,
        message: ERROR_MESSAGE_CHECK_CONNECTION
      };
    case RoomLayoutErrorCode.CLIENT_ERROR:
    case RoomLayoutErrorCode.SERVER_ERROR:
    case RoomLayoutErrorCode.UNKNOWN:
    default:
      return {
        title: ERROR_TITLE_SOMETHING_WRONG,
        message: error.message ?? ERROR_FALLBACK_MESSAGE
      };
  }
}

/** Phase 1: returns layout as JSON; interactive UI in later phases. Spots with status BOOKABLE are selectable. */
const LOADING_SKIP_THRESHOLD_MS = 10_000;

export default function RoomLayoutMap({
  envUrl,
  layoutId,
  userToken,
  timeSlotId,
  reservedSpotKeys,
  onSuccess,
  onError,
  onSpotSelect,
  onSkipLoadingAvailable,
  onErrorStateChange,
  onRefetchReady,
  onBookableAvailabilityChange,
  renderLoading,
  selectedSpotKey: controlledSelectedSpotKey,
  assignedSpotKey: controlledAssignedSpotKey,
  selectedSpotColor,
  loadingMinimumMs,
  pinchToZoomEnabled = false,
  pinchZoomSlotHeight,
  hideLegend = false,
  pinchEmbedInPageScroll = false,
  useCustomNonBookablePrefixes = false
}: RoomLayoutMapProps) {
  const [internalSelectedSpotKey, setInternalSelectedSpotKey] = useState<string | null>(null);

  const isControlledSelection = controlledSelectedSpotKey !== undefined;

  const selectedSpotKey = isControlledSelection
    ? (controlledSelectedSpotKey ?? null)
    : internalSelectedSpotKey;

  const assignedSpotKey = controlledAssignedSpotKey ?? null;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const callbacks = useMemo(
    () => ({
      onSuccess: (response: RoomLayoutApiResponse) => onSuccessRef.current?.(response),
      onError: (err: RoomLayoutError) => onErrorRef.current?.(err),
      ...(loadingMinimumMs !== undefined && { loadingMinimumMs })
    }),
    [loadingMinimumMs]
  );

  const { data, isLoading, isError, error, refetch, isErrorRef } = useRoomLayoutMap(
    layoutId,
    envUrl,
    userToken,
    callbacks,
    timeSlotId !== undefined && timeSlotId !== '' ? timeSlotId : undefined
  );

  const onSkipLoadingAvailableRef = useRef(onSkipLoadingAvailable);
  onSkipLoadingAvailableRef.current = onSkipLoadingAvailable;
  const onErrorStateChangeRef = useRef(onErrorStateChange);
  onErrorStateChangeRef.current = onErrorStateChange;
  const onRefetchReadyRef = useRef(onRefetchReady);
  onRefetchReadyRef.current = onRefetchReady;
  const onBookableAvailabilityChangeRef = useRef(onBookableAvailabilityChange);
  onBookableAvailabilityChangeRef.current = onBookableAvailabilityChange;
  const onSpotSelectRef = useRef(onSpotSelect);
  onSpotSelectRef.current = onSpotSelect;

  useEffect(() => {
    if (!isLoading) {
      onSkipLoadingAvailableRef.current?.(false);
      return;
    }
    const timer = setTimeout(() => {
      if (!isErrorRef.current) {
        onSkipLoadingAvailableRef.current?.(true);
      }
    }, LOADING_SKIP_THRESHOLD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [isLoading, isErrorRef]);

  useEffect(() => {
    onErrorStateChangeRef.current?.(isError, error ?? null);
  }, [isError, error]);

  useEffect(() => {
    onRefetchReadyRef.current?.(refetch);
  }, [refetch]);

  const {
    scrollRef: horizontalScrollRef,
    onLayout: onHorizontalScrollLayout,
    onContentSizeChange: onHorizontalScrollContentSizeChange,
    reset: resetHorizontalScroll
  } = useScrollToCenter();
  const [gridContentWidth, setGridContentWidth] = useState(0);
  const [gridContentHeight, setGridContentHeight] = useState(0);
  const gridMetricsLayoutIdRef = useRef(layoutId);
  const layoutIdRef = useRef(layoutId);
  layoutIdRef.current = layoutId;
  const [pinchOverlayHeight, setPinchOverlayHeight] = useState(0);
  const [legendHeight, setLegendHeight] = useState(0);
  const gridMetricsMatchLayout = gridMetricsLayoutIdRef.current === layoutId;
  const effectiveGridContentWidth = gridMetricsMatchLayout ? gridContentWidth : 0;
  const effectiveGridContentHeight = gridMetricsMatchLayout ? gridContentHeight : 0;
  const hasExternalPinchSlotHeight = pinchZoomSlotHeight != null && pinchZoomSlotHeight > 0;
  const shouldMeasurePinchOverlay =
    pinchToZoomEnabled && (!pinchEmbedInPageScroll || !hasExternalPinchSlotHeight);

  useLayoutEffect(() => {
    resetHorizontalScroll();
    if (gridMetricsLayoutIdRef.current !== layoutId) {
      gridMetricsLayoutIdRef.current = layoutId;
      setGridContentWidth(0);
      setGridContentHeight(0);
      setLegendHeight(0);
      setPinchOverlayHeight(0);
    }
  }, [layoutId, resetHorizontalScroll]);

  useLayoutEffect(() => {
    if (pinchToZoomEnabled) return;
    resetHorizontalScroll();
  }, [pinchToZoomEnabled, resetHorizontalScroll]);

  const onPinchOverlayLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      setPinchOverlayHeight(e.nativeEvent.layout.height);
    },
    [layoutId]
  );

  const onLegendLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      setLegendHeight(e.nativeEvent.layout.height);
    },
    [layoutId]
  );

  const pinchOverlayHeightForSlot =
    pinchZoomSlotHeight != null && pinchZoomSlotHeight > 0
      ? pinchZoomSlotHeight
      : pinchOverlayHeight;

  const effectiveLegendHeight = hideLegend ? 0 : legendHeight;

  const pinchZoomMaxSlotHeight = useMemo(
    () => getPinchZoomMaxSlotHeight(pinchOverlayHeightForSlot, effectiveLegendHeight, hideLegend),
    [pinchOverlayHeightForSlot, effectiveLegendHeight, hideLegend]
  );

  const zoomViewportHeight = useMemo(
    () =>
      getZoomViewportHeight(
        effectiveGridContentHeight,
        pinchZoomMaxSlotHeight,
        pinchEmbedInPageScroll
      ),
    [effectiveGridContentHeight, pinchZoomMaxSlotHeight, pinchEmbedInPageScroll]
  );

  const shouldClampPinchViewport =
    pinchToZoomEnabled &&
    effectiveGridContentHeight > 0 &&
    zoomViewportHeight != null &&
    zoomViewportHeight > 0;

  const onGridContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      const { width, height } = e.nativeEvent.layout;
      if (width <= 0 || height <= 0) return;
      gridMetricsLayoutIdRef.current = layoutId;
      setGridContentWidth(width);
      setGridContentHeight(height);
    },
    [layoutId]
  );

  const onSpotPress = useCallback(
    (spot: RoomLayoutSpot) => {
      if (spot.status !== SpotStatus.BOOKABLE) return;
      if (!isControlledSelection) {
        setInternalSelectedSpotKey(spot.spotName);
      }
      onSpotSelectRef.current?.(spot);
    },
    [isControlledSelection]
  );

  const layoutSpots = useMemo(
    () => enrichSpotsWithLayoutSpotTypePrefix(data?.spots ?? [], data?.spot_types),
    [data?.spot_types, data?.spots]
  );

  const spotTypeItems = useMemo(() => {
    if (!layoutSpots.length) {
      if (data?.spot_types?.length) {
        return spotTypeLegendItemsFromSpotTypesForBookableSpots(data.spot_types, layoutSpots);
      }
      return undefined;
    }
    const derived = deriveSpotTypeLegendItemsFromSpots(layoutSpots);
    if (derived !== undefined) return derived;
    if (data?.spot_types?.length)
      return spotTypeLegendItemsFromSpotTypesForBookableSpots(data.spot_types, layoutSpots);
    return undefined;
  }, [data?.spot_types, layoutSpots]);

  const resolvedAssignedSpotKey = useMemo(
    () => resolveSpotKeyInLayout(layoutSpots, assignedSpotKey),
    [layoutSpots, assignedSpotKey]
  );

  const resolvedSelectedSpotKey = useMemo(
    () => resolveSpotKeyInLayout(layoutSpots, selectedSpotKey) ?? selectedSpotKey,
    [layoutSpots, selectedSpotKey]
  );

  const isChangeSpotMap = resolvedAssignedSpotKey !== null;

  const spotsToShow = useMemo(
    (): RoomLayoutSpot[] =>
      applySpotsOverlayForMap(layoutSpots, reservedSpotKeys, resolvedAssignedSpotKey),
    [layoutSpots, reservedSpotKeys, resolvedAssignedSpotKey]
  );

  const bookableAvailability = useMemo(() => countBookableSpots(spotsToShow), [spotsToShow]);
  const { bookableCount, hasBookableSpots } = bookableAvailability;

  useEffect(() => {
    if (isLoading || isError || !data) return;
    onBookableAvailabilityChangeRef.current?.({ bookableCount, hasBookableSpots });
  }, [isLoading, isError, data, bookableCount, hasBookableSpots]);

  // In uncontrolled mode, clear selection when the selected spot becomes BLOCKED (e.g. reserved by someone else).
  // Check reserved keys directly so we clear even when the parent passes a new array reference without spotsToShow updating first.
  useEffect(() => {
    if (isControlledSelection) return;
    const key = internalSelectedSpotKey;
    if (key === null) return;
    const selectedSpot = spotsToShow.find((s) => s.spotName === key);
    const isMemberAssignment =
      selectedSpot !== undefined &&
      resolvedAssignedSpotKey !== null &&
      spotMatchesKey(selectedSpot, resolvedAssignedSpotKey);
    const ids = reservedSpotKeys ?? [];
    if (!isMemberAssignment && ids.some((id) => String(id) === key)) {
      setInternalSelectedSpotKey(null);
      return;
    }
    if (selectedSpot?.status === SpotStatus.BLOCKED) {
      setInternalSelectedSpotKey(null);
    }
  }, [
    spotsToShow,
    reservedSpotKeys,
    internalSelectedSpotKey,
    isControlledSelection,
    resolvedAssignedSpotKey
  ]);

  const layoutColumns = data?.columns ?? 0;
  const layoutRows = data?.rows ?? 0;

  const renderGrid = useCallback(
    () => (
      <RoomLayoutGrid
        columns={layoutColumns}
        rows={layoutRows}
        spots={spotsToShow}
        selectedSpotKey={resolvedSelectedSpotKey}
        assignedSpotKey={resolvedAssignedSpotKey}
        {...(!isChangeSpotMap && selectedSpotColor !== undefined ? { selectedSpotColor } : {})}
        onSpotPress={onSpotPress}
        scrollable
        useCustomNonBookablePrefixes={useCustomNonBookablePrefixes}
      />
    ),
    [
      layoutColumns,
      layoutRows,
      spotsToShow,
      resolvedSelectedSpotKey,
      resolvedAssignedSpotKey,
      isChangeSpotMap,
      selectedSpotColor,
      onSpotPress,
      useCustomNonBookablePrefixes
    ]
  );

  const renderPinchGridContent = useCallback(
    () => (
      <View onLayout={onGridContentLayout} testID="room-layout-grid-pinch-content">
        {renderGrid()}
      </View>
    ),
    [onGridContentLayout, renderGrid]
  );

  /** Web/UMD: zoom viewport is a passthrough — keep legacy horizontal scroll for wide grids. */
  const renderGridInPinchZoom = useCallback(() => {
    const gridContent = renderPinchGridContent();
    if (Platform.OS !== 'web') {
      return gridContent;
    }

    return (
      <View onLayout={onHorizontalScrollLayout}>
        <ScrollView
          ref={horizontalScrollRef}
          horizontal
          nestedScrollEnabled
          style={styles.gridZoomHorizontalScroll}
          contentContainerStyle={styles.horizontalScrollContent}
          showsHorizontalScrollIndicator
          onContentSizeChange={onHorizontalScrollContentSizeChange}
          testID="room-layout-grid-horizontal-scroll-pinch"
        >
          {gridContent}
        </ScrollView>
      </View>
    );
  }, [
    renderPinchGridContent,
    onHorizontalScrollLayout,
    horizontalScrollRef,
    onHorizontalScrollContentSizeChange
  ]);

  return (
    <View
      style={[
        styles.container,
        pinchToZoomEnabled && !pinchEmbedInPageScroll && styles.containerPinchZoom
      ]}
      testID="room-layout-map"
    >
      {isLoading && (
        <View testID="room-layout-map-loading">
          {renderLoading ? renderLoading() : <Text>{RoomLayoutMapStrings.LOADING_LAYOUT}</Text>}
        </View>
      )}

      {isError && error && (
        <ErrorState {...errorToTitleMessage(error)} testID="room-layout-map-error" />
      )}

      {!isLoading && !isError && data && (
        <View
          style={[
            styles.contentWithOverlay,
            shouldMeasurePinchOverlay &&
              !pinchEmbedInPageScroll &&
              styles.contentWithOverlayPinchZoom
          ]}
          onLayout={shouldMeasurePinchOverlay ? onPinchOverlayLayout : undefined}
        >
          {pinchToZoomEnabled ? (
            <View
              style={[styles.gridZoomSlot, pinchEmbedInPageScroll && styles.gridZoomSlotPageScroll]}
            >
              <View
                style={[
                  styles.gridScrollContainer,
                  !pinchEmbedInPageScroll &&
                    !shouldClampPinchViewport &&
                    styles.gridZoomViewportFill,
                  shouldClampPinchViewport && styles.gridZoomViewport,
                  shouldClampPinchViewport && { height: zoomViewportHeight }
                ]}
              >
                <SpotGridZoomViewportLazy
                  enabled
                  resetKey={layoutId}
                  contentWidth={effectiveGridContentWidth}
                  contentHeight={effectiveGridContentHeight}
                  fallback={renderPinchGridContent()}
                  testID="room-layout-grid-zoom-viewport"
                >
                  {renderGridInPinchZoom()}
                </SpotGridZoomViewportLazy>
              </View>
            </View>
          ) : (
            <View style={styles.gridScrollContainer} onLayout={onHorizontalScrollLayout}>
              <ScrollView
                ref={horizontalScrollRef}
                horizontal
                contentContainerStyle={styles.horizontalScrollContent}
                showsHorizontalScrollIndicator={Platform.OS === 'web'}
                onContentSizeChange={onHorizontalScrollContentSizeChange}
                testID="room-layout-grid-horizontal-scroll"
              >
                {renderGrid()}
              </ScrollView>
            </View>
          )}
          {!hideLegend ? (
            <View
              style={styles.legendSpacer}
              onLayout={pinchToZoomEnabled ? onLegendLayout : undefined}
            >
              <SpotLegend
                {...(spotTypeItems !== undefined ? { spotTypeItems } : {})}
                {...(selectedSpotColor !== undefined ? { selectedSpotColor } : {})}
              />
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
}
